import {
  EndpointsEndpointResponse,
  PortainereeCloudProvider,
  PortainereeEndpointStatusMessage,
  PortainerDockerSnapshot,
  PortainerEnvironmentEdgeSettings,
  PortainereeEndpointPolicies,
  PortainereeAppliedPolicy,
  PortainereePolicyStatusType,
  PortainereeKubernetesData,
  PortainerKubernetesStorageClassConfig,
} from '@api/types.gen';

import { DockerSnapshot } from '@/react/docker/snapshots/types';

import {
  AppliedPolicy,
  AppliedPolicyStatusType,
  CloudProviderSettings,
  ContainerEngine,
  Environment,
  EnvironmentEdge,
  EnvironmentId,
  EnvironmentPolicies,
  EnvironmentStatusMessage,
  EnvironmentStatus,
  EnvironmentType,
  K8sDistributionType,
  KaasProvider,
  KubernetesSettings,
  StorageClass,
} from '../types';

export function buildUrl(id?: EnvironmentId, action?: string) {
  let baseUrl = 'endpoints';
  if (id) {
    baseUrl += `/${id}`;
  }

  if (action) {
    baseUrl += `/${action}`;
  }

  return baseUrl;
}

export function toEnvironment(
  endpoint: EndpointsEndpointResponse
): Environment {
  return {
    ...endpoint,
    Type: endpoint.Type ? endpoint.Type : EnvironmentType.Docker,
    Status: endpoint.Status ?? EnvironmentStatus.Down,
    ContainerEngine: toContainerEngine(endpoint.ContainerEngine),
    TagIds: endpoint.TagIds ?? [],
    Gpus: endpoint.Gpus ?? [],
    EnableGPUManagement: endpoint.EnableGPUManagement ?? false,
    Snapshots: endpoint.Snapshots?.map(toDockerSnapshot) ?? [],
    Edge: toEdge(endpoint.Edge),
    Kubernetes: toKubernetesSettings(endpoint.Kubernetes),
    CloudProvider: toCloudProvider(endpoint.CloudProvider),
    DeploymentOptions: endpoint.DeploymentOptions ?? null,
    StatusMessage: toStatusMessage(endpoint.StatusMessage),
    Policies: toPolicies(endpoint.Policies),
  };
}

function toKubernetesSettings(
  data: PortainereeKubernetesData
): KubernetesSettings {
  return {
    ...data,
    Configuration: {
      ...data.Configuration,
      StorageClasses:
        data.Configuration?.StorageClasses?.map(toStorageClass) ?? [],
      IngressClasses: data.Configuration?.IngressClasses ?? [],
    },
  };

  function toStorageClass(
    original: PortainerKubernetesStorageClassConfig
  ): StorageClass {
    return {
      ...original,
      AccessModes: original.AccessModes ?? [],
    };
  }
}

function toPolicies(
  policies: PortainereeEndpointPolicies | undefined
): EnvironmentPolicies | undefined {
  if (!policies) return undefined;
  return {
    changeConfirmationPolicy: toAppliedPolicy(
      policies.changeConfirmationPolicy
    ),
    dockerCleanupPolicy: toAppliedPolicy(policies.dockerCleanupPolicy),
    dockerSecurityPolicy: toAppliedPolicy(policies.dockerSecurityPolicy),
    dockerSetupPolicy: toAppliedPolicy(policies.dockerSetupPolicy),
    dockerRBACPolicy: toAppliedPolicy(policies.dockerRBACPolicy),
    dockerRegistryPolicy: toAppliedPolicy(policies.dockerRegistryPolicy),
    k8sSecurityPolicy: toAppliedPolicy(policies.k8sSecurityPolicy),
    k8sSetupPolicy: toAppliedPolicy(policies.k8sSetupPolicy),
    k8sRBACPolicy: toAppliedPolicy(policies.k8sRBACPolicy),
    k8sRegistryPolicy: toAppliedPolicy(policies.k8sRegistryPolicy),
    k8sObservabilityPolicy: toAppliedPolicy(policies.k8sObservabilityPolicy),
  } satisfies Record<
    keyof Required<PortainereeEndpointPolicies>,
    AppliedPolicy
  >;

  function toAppliedPolicy(
    policy: PortainereeAppliedPolicy | undefined
  ): AppliedPolicy {
    return {
      policyID: policy?.policyID,
      policyName: policy?.policyName,
      policyStatus: {
        Message: policy?.policyStatus?.Message ?? '',
        Status: toPolicyStatusType(policy?.policyStatus?.Status),
        LastAttemptTime: policy?.policyStatus?.LastAttemptTime,
      },
    };

    function toPolicyStatusType(
      status: PortainereePolicyStatusType | undefined
    ): AppliedPolicyStatusType {
      switch (status) {
        case PortainereePolicyStatusType.POLICY_STATUS_TYPE_APPLIED:
          return AppliedPolicyStatusType.Applied;
        case PortainereePolicyStatusType.POLICY_STATUS_TYPE_FAILED:
          return AppliedPolicyStatusType.Failed;
        case PortainereePolicyStatusType.POLICY_STATUS_TYPE_IN_PROGRESS:
          return AppliedPolicyStatusType.InProgress;
        case PortainereePolicyStatusType.POLICY_STATUS_TYPE_WARNING:
          return AppliedPolicyStatusType.Warning;
        case PortainereePolicyStatusType.POLICY_STATUS_TYPE_NOT_SUPPORTED:
          return AppliedPolicyStatusType.NotSupported;
        case undefined:
          return AppliedPolicyStatusType.NotSupported;
      }
    }
  }
}

function toContainerEngine(engine: string): ContainerEngine {
  switch (engine) {
    case ContainerEngine.Docker:
      return ContainerEngine.Docker;
    case ContainerEngine.Podman:
      return ContainerEngine.Podman;
    case '':
      return ContainerEngine.Kubernetes;
    default:
      return ContainerEngine.Docker;
  }
}

function toEdge(edge?: PortainerEnvironmentEdgeSettings): EnvironmentEdge {
  return {
    AsyncMode: edge?.AsyncMode ?? false,
    PingInterval: edge?.PingInterval ?? 0,
    SnapshotInterval: edge?.SnapshotInterval ?? 0,
    CommandInterval: edge?.CommandInterval ?? 0,
  };
}

function toCloudProvider(
  provider: PortainereeCloudProvider | undefined
): Partial<CloudProviderSettings> | undefined {
  if (!provider) {
    return undefined;
  }

  return {
    Name: toCloudProviderName(provider.Name),
    Provider: toCloudProviderType(provider.Provider),
    URL: provider.URL,
    Region: provider.Region,
    NodeCount: provider.NodeCount,
    CredentialID: provider.CredentialID,
    DNSPrefix: provider.DNSPrefix,
    PoolName: provider.PoolName,
    ResourceGroup: provider.ResourceGroup,
    Tier: provider.Tier,
    KubernetesVersion: provider.KubernetesVersion,
  };
}

function toCloudProviderName(
  name: string | undefined
): CloudProviderSettings['Name'] {
  switch (name) {
    case 'Civo':
      return 'Civo';
    case 'Linode':
      return 'Linode';
    case 'Digital Ocean':
      return 'Digital Ocean';
    case 'Google':
      return 'Google';
    case 'Azure':
      return 'Azure';
    case 'Amazon':
      return 'Amazon';
    default:
    case undefined:
      return 'Civo';
  }
}

function toCloudProviderType(
  provider: string | undefined
): CloudProviderSettings['Provider'] {
  switch (provider) {
    case 'civo':
      return KaasProvider.CIVO;
    case 'linode':
      return KaasProvider.LINODE;
    case 'digitalocean':
      return KaasProvider.DIGITAL_OCEAN;
    case 'gke':
      return KaasProvider.GOOGLE_CLOUD;
    case 'amazon':
      return KaasProvider.AWS;
    case 'azure':
      return KaasProvider.AZURE;
    case 'omni':
      return K8sDistributionType.OMNI;
    default:
    case undefined:
      return KaasProvider.CIVO;
  }
}

function toStatusMessage(
  msg: PortainereeEndpointStatusMessage
): EnvironmentStatusMessage {
  return {
    detail: msg.detail ?? '',
    summary: msg.summary ?? '',
    warnings: msg.warnings ?? null,
    operation: toOperation(msg.operation),
    operationStatus: msg.operationStatus ?? '',
  };
}

function toOperation(
  operation: string | undefined
): EnvironmentStatusMessage['operation'] {
  switch (operation) {
    case 'scale':
      return 'scale';
    case 'upgrade':
      return 'upgrade';
    case 'addons':
      return 'addons';
    case undefined:
    default:
      return '';
  }
}

function toDockerSnapshot(snapshot: PortainerDockerSnapshot): DockerSnapshot {
  return {
    ...snapshot,
    GpuUseList: snapshot.GpuUseList ?? [],
  };
}
